import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { checkStaffLogin, createStaff, fetchLoginSuggestions } from './staffApi';
import {
  LoginCheck,
  LoginSuggestion,
  StaffCreated,
  StaffCreateInput,
  StaffKind,
  StaffPoint,
  StaffTarget,
  StaffTreeModule
} from './types';
import {
  MAX_NAME_LENGTH,
  MIN_NAME_LENGTH,
  nameLength,
  normalizeSelection,
  normalizeTail,
  passwordProblem,
  tailProblem
} from './staffUtils';
import StaffPermissionTree from './StaffPermissionTree';
import {
  STAFF_PRIMARY,
  dialogIconSx,
  dialogPaperSx,
  dialogTitleSx,
  kindToggleSx,
  outlinedButtonSx,
  primaryButtonSx,
  sectionTitleSx
} from './staffStyles';

interface StaffCreateDialogProps {
  open: boolean;
  target: StaffTarget;
  point: StaffPoint;
  tree: StaffTreeModule[];
  onClose: () => void;
  onCreated: (created: StaffCreated, input: StaffCreateInput) => void;
}

/** Пауза ввода перед запросом подбора и проверки логина */
const TYPING_DELAY_MS = 400;

const KIND_HINTS: Record<StaffKind, string> = {
  person: 'Учётка одного человека: ФИО, свой пароль, при первом входе сотрудник задаёт пароль сам',
  shared: 'Общая учётка смены или кассы: название вместо ФИО, пароль знает несколько человек'
};

type CheckState = { status: 'idle' } | { status: 'checking' } | { status: 'done'; result: LoginCheck } | { status: 'failed' };

const StaffCreateDialog: React.FC<StaffCreateDialogProps> = ({ open, target, point, tree, onClose, onCreated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [kind, setKind] = useState<StaffKind>('person');
  const [fullName, setFullName] = useState('');
  const [tail, setTail] = useState('');
  // Пока окончание логина не правили руками, его подставляет первый вариант подбора
  const [tailTouched, setTailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<LoginSuggestion[]>([]);
  const [check, setCheck] = useState<CheckState>({ status: 'idle' });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ответ на устаревший запрос (имя или логин уже изменились) не должен перетирать актуальный
  const suggestRequest = useRef(0);
  const checkRequest = useRef(0);

  useEffect(() => {
    if (!open) return;
    setKind('person');
    setFullName('');
    setTail('');
    setTailTouched(false);
    setPassword('');
    setShowPassword(false);
    setPermissions([]);
    setSuggestions([]);
    setCheck({ status: 'idle' });
    setSubmitted(false);
    setSaving(false);
    setError(null);
  }, [open]);

  const trimmedName = fullName.trim().replace(/\s+/g, ' ');
  const nameOk = nameLength(fullName) >= MIN_NAME_LENGTH && nameLength(fullName) <= MAX_NAME_LENGTH;

  useEffect(() => {
    if (!open) return undefined;
    const request = ++suggestRequest.current;
    if (!nameOk) {
      setSuggestions([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      fetchLoginSuggestions(target, trimmedName)
        .then((rows) => {
          if (request !== suggestRequest.current) return;
          setSuggestions(rows);
          if (!tailTouched && rows.length > 0) setTail(rows[0].tail);
        })
        // Подбор - подсказка: без него окончание логина вводится вручную
        .catch(() => { if (request === suggestRequest.current) setSuggestions([]); });
    }, TYPING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open, target, trimmedName, nameOk, tailTouched]);

  const tailError = tailProblem(tail);

  useEffect(() => {
    if (!open) return undefined;
    const request = ++checkRequest.current;
    if (!tail || tailError) {
      setCheck({ status: 'idle' });
      return undefined;
    }
    setCheck({ status: 'checking' });
    const timer = window.setTimeout(() => {
      checkStaffLogin(target, tail)
        .then((result) => { if (request === checkRequest.current) setCheck({ status: 'done', result }); })
        .catch(() => { if (request === checkRequest.current) setCheck({ status: 'failed' }); });
    }, TYPING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open, target, tail, tailError]);

  const login = tail ? `${point.login}.${tail}` : '';
  const passwordError = passwordProblem(password, login);
  const loginTaken = check.status === 'done' && !check.result.available;

  const nameHelper = (submitted && !nameOk)
    ? `От ${MIN_NAME_LENGTH} до ${MAX_NAME_LENGTH} символов`
    : (kind === 'person' ? 'Фамилия и имя сотрудника' : 'Например: Касса 1 или Ночная смена');

  let tailHelper = 'Латиница, цифры, «_» и «-»';
  if (tailError) tailHelper = tailError;
  else if (submitted && !tail) tailHelper = 'Укажите окончание логина';
  else if (check.status === 'checking') tailHelper = 'Проверяем логин...';
  else if (check.status === 'failed') tailHelper = 'Не удалось проверить логин - сервер проверит его при создании';
  else if (check.status === 'done') tailHelper = check.result.message || (check.result.available ? `Логин ${login} свободен` : `Логин ${login} занят`);

  const handleSubmit = async () => {
    setSubmitted(true);
    setError(null);
    if (!nameOk || !tail || tailError || passwordError || loginTaken) return;
    const input: StaffCreateInput = {
      kind,
      fullName: trimmedName,
      tail,
      password,
      permissions: normalizeSelection(permissions, tree)
    };
    setSaving(true);
    try {
      const created = await createStaff(target, input);
      onCreated(created, input);
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось создать сотрудника'));
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={fullScreen} PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={dialogTitleSx}>
        <Box sx={dialogIconSx}>
          <PersonAddAlt1OutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.25 }}>Новый сотрудник</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }} noWrap>{point.name}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={saving} sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>}

        <Box>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={kind}
            onChange={(_e, value: StaffKind | null) => { if (value) setKind(value); }}
            disabled={saving}
          >
            <ToggleButton value="person" sx={kindToggleSx}>Сотрудник</ToggleButton>
            <ToggleButton value="shared" sx={kindToggleSx}>Общая учётка</ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.75 }}>{KIND_HINTS[kind]}</Typography>
        </Box>

        <TextField
          label={kind === 'person' ? 'ФИО' : 'Название'}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={submitted && !nameOk}
          helperText={nameHelper}
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          disabled={saving}
          size="small"
          fullWidth
          autoFocus
        />

        <Box>
          <TextField
            label="Логин"
            value={tail}
            onChange={(e) => {
              setTailTouched(true);
              setTail(normalizeTail(e.target.value));
            }}
            error={Boolean(tailError) || loginTaken || (submitted && !tail)}
            helperText={tailHelper}
            FormHelperTextProps={{ sx: { color: check.status === 'done' && check.result.available && !tailError ? '#059669' : undefined } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.25, '& .MuiTypography-root': { color: '#64748b', fontFamily: 'Consolas, "Roboto Mono", monospace' } }}>
                  {`${point.login}.`}
                </InputAdornment>
              ),
              endAdornment: check.status === 'checking' ? (
                <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
              ) : undefined,
              sx: { fontFamily: 'Consolas, "Roboto Mono", monospace' }
            }}
            inputProps={{ maxLength: 30, autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
            disabled={saving}
            size="small"
            fullWidth
          />
          {suggestions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Свободные варианты:</Typography>
              {suggestions.map(item => (
                <Chip
                  key={item.tail}
                  label={item.login}
                  size="small"
                  disabled={saving}
                  onClick={() => {
                    setTailTouched(true);
                    setTail(item.tail);
                  }}
                  sx={{
                    height: 24,
                    borderRadius: '6px',
                    fontFamily: 'Consolas, "Roboto Mono", monospace',
                    fontSize: '0.76rem',
                    bgcolor: item.tail === tail ? '#eff6ff' : '#f1f5f9',
                    color: item.tail === tail ? STAFF_PRIMARY : '#475569',
                    border: '1px solid',
                    borderColor: item.tail === tail ? '#bfdbfe' : 'transparent'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <TextField
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={Boolean(passwordError)}
          helperText={passwordError || 'Оставьте пустым - пароль сгенерируется и будет показан один раз'}
          autoComplete="new-password"
          disabled={saving}
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}>
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={sectionTitleSx}>Доступные разделы</Typography>
          <StaffPermissionTree tree={tree} value={normalizeSelection(permissions, tree)} onChange={setPermissions} disabled={saving} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button variant="outlined" onClick={handleClose} disabled={saving} sx={outlinedButtonSx}>
          Отмена
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={primaryButtonSx}
        >
          Создать
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffCreateDialog;
